/**
 * ExecutionGuideStepper — Procedimento corporativo orientado à ação.
 */
import React, { useMemo } from 'react';
import { BookOpen, CheckCircle2, Circle, TriangleAlert } from 'lucide-react';

const ACTION_VERB_REGEX = /^(Cadastrar|Vincular|Definir|Preencher|Verificar|Conferir|Selecionar|Inserir|Registrar|Confirmar|Checar|Abrir|Fechar|Salvar|Enviar|Criar|Atualizar|Lançar|Validar|Informar|Assinar|Anexar|Revisar|Acionar|Marcar|Descrever|Identificar|Calcular|Aprovar|Liberar|Realizar|Acessar|Digitalizar|Imprimir|Comunicar|Notificar|Classificar|Solicitar|Executar|Pagar|Cobrar|Encaminhar|Retornar)\b/;

export interface GuideStep {
  id: string;
  order: number;
  title: string;
  description?: string;
}

function extractSteps(instruction: string): GuideStep[] {
  const lines = instruction.split('\n').map((l) => l.trim()).filter(Boolean);
  const steps: GuideStep[] = [];
  let order = 0;

  for (const line of lines) {
    const numbered = line.match(/^(?:#{1,3}\s*)?(?:PASSO\s*)?(\d+)[.)]\s*(.+)$/i);
    const bullet = line.match(/^[-*•]\s*(?:\[[ xX]\]\s*)?(.+)$/);
    const checkbox = line.match(/^\[[ xX]\]\s*(.+)$/);
    let title: string | null = null;
    if (numbered) title = numbered[2].trim();
    else if (checkbox) title = checkbox[1].trim();
    else if (bullet) title = bullet[1].trim();
    else if (ACTION_VERB_REGEX.test(line)) title = line;

    if (title) {
      order += 1;
      steps.push({
        id: `step-${order}`,
        order,
        title: title.length > 72 ? `${title.slice(0, 69)}…` : title,
        description: title.length > 72 ? title : undefined,
      });
    }
  }

  if (steps.length === 0) {
    instruction.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean).forEach((block, i) => {
      const firstLine = block.split('\n')[0]?.trim() ?? block;
      steps.push({
        id: `block-${i + 1}`,
        order: i + 1,
        title: firstLine.length > 72 ? `${firstLine.slice(0, 69)}…` : firstLine,
      });
    });
  }

  return steps.slice(0, 12);
}

interface ExecutionGuideStepperProps {
  instruction: string | null;
  activeStep?: number;
  onRequestSop?: () => void;
  onReportProblem?: () => void;
}

export const ExecutionGuideStepper: React.FC<ExecutionGuideStepperProps> = ({
  instruction,
  activeStep = 1,
  onRequestSop,
  onReportProblem,
}) => {
  const steps = useMemo(() => (instruction ? extractSteps(instruction) : []), [instruction]);

  if (!instruction) {
    return (
      <div className="mx-3 my-3 rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="flex items-start gap-2">
          <BookOpen size={16} className="mt-0.5 shrink-0 text-slate-400" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600 dark:text-slate-300">
              Sem procedimento operacional
            </p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-slate-600 dark:text-slate-400">
              Esta etapa não possui orientação operacional cadastrada. Você pode solicitar documentação ou registrar um problema para revisão do processo.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {onRequestSop && (
                <button
                  type="button"
                  onClick={onRequestSop}
                  className="rounded-sm border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-[10px] font-semibold text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300"
                >
                  Solicitar SOP
                </button>
              )}
              {onReportProblem && (
                <button
                  type="button"
                  onClick={onReportProblem}
                  className="inline-flex items-center gap-1 rounded-sm border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[10px] font-semibold text-rose-700 hover:bg-rose-100 dark:border-rose-900/40 dark:text-rose-300"
                >
                  <TriangleAlert size={11} />
                  Registrar Problema
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <p className="px-3 py-4 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
        {instruction}
      </p>
    );
  }

  return (
    <ol className="space-y-0">
      {steps.map((step) => {
        const isDone = step.order < activeStep;
        const isCurrent = step.order === activeStep;
        return (
          <li
            key={step.id}
            className={`relative flex gap-3 border-l-2 py-3 pl-4 pr-3 ${
              isCurrent
                ? 'border-l-[#2F6FED] bg-blue-50/60 dark:border-l-blue-500 dark:bg-blue-950/20'
                : isDone
                  ? 'border-l-emerald-500'
                  : 'border-l-slate-200 dark:border-l-slate-700'
            }`}
          >
            <span className="mt-0.5 shrink-0">
              {isDone ? (
                <CheckCircle2 size={16} className="text-emerald-500" />
              ) : (
                <Circle size={16} className={isCurrent ? 'text-[#2F6FED]' : 'text-slate-300'} />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400">Passo {step.order}</p>
              <p className={`mt-0.5 text-[12px] font-semibold leading-snug ${isCurrent ? 'text-blue-900 dark:text-blue-200' : 'text-slate-800 dark:text-slate-200'}`}>
                {step.title}
              </p>
              {step.description && (
                <p className="mt-1 text-[10px] leading-relaxed text-slate-500">{step.description}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default ExecutionGuideStepper;
